import {
  ControlPanelPage,
  EditorComponentPageLoader,
  EditorPage,
  EditorPageContextProvider,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityGroupProfileCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityMembersListCard,
  EntityOrphanWarning,
  EntityOwnershipCard,
  EntityProcessingErrorsPanel,
  EntitySwitch,
  EntityUserProfileCard,
  hasCatalogProcessingErrors,
  hasReservedFieldsError,
  isComponentType,
  isKind,
  isServiceAccount,
  isOrphan,
  ReleasePage,
  ReleasePageContextProvider,
  ReservedFieldsError,
} from '@agilelab/plugin-wb-builder-catalog';
import {
  validateWitboostVersionedEntity,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  BuilderCustomPagesEnum,
  createRefreshEntityFunction,
  customAlertApiRef,
  customFrontendApiRef,
  CustomMicrofrontendComponent,
  CustomMicrofrontendOptions,
  CustomView,
  useBuilderCustomPagesConfig,
  useMicrofrontendVisibility,
  useRefreshPermission,
  WbEnvironmentSelector,
  WbHeaderActions,
} from '@agilelab/plugin-wb-platform';
import { BELONGS_TO } from '@agilelab/plugin-wb-practice-shaper-common';
import {
  Entity,
  RELATION_HAS_PART,
  RELATION_PART_OF,
} from '@mainstage/catalog-model';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { EntityTechdocsContent } from '@agilelab/plugin-wb-techdocs';
import Grid from '@material-ui/core/Grid';
import { useEffect, useMemo, useState } from 'react';
import {
  catalogApiRef,
  getEntityRelations,
  useEntity,
  useRelatedEntities,
} from '@mainstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { EntityLayoutWrapper } from './EntityLayoutWrapper';
import { ProjectPage } from './ProjectPage';
import { TechdocsWrapper } from './TechdocsWrapper';
import {
  AccessTokenCard,
  GitTokensCard,
} from '@agilelab/backstage-plugin-wb-service-accounts';

const techdocsContent = <EntityTechdocsContent />;
const EntityAboutCard = ({ customviewpage }: { customviewpage?: string }) => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const { allowRefresh } = useRefreshPermission(entity);
  const refreshEntity = createRefreshEntityFunction({
    entity,
    catalogApi,
    alertApi,
  });

  return (
    <CustomView
      id={customviewpage ? customviewpage : 'builder_entity_content'}
      transform="builder_system"
      typeId={String(entity.spec?.type ?? '')}
      data={{
        entity: entity as WitboostSystem,
        allowRefresh,
      }}
      actions={{
        refreshEntity: refreshEntity,
      }}
    />
  );
};
/**
 * Checks whether the provided entity is an headless system
 */
export const isHeadlessComponent = async (entity: Entity) => {
  return (
    entity.kind.toLocaleLowerCase('en-US') === 'component' &&
    entity.relations?.find(relation => relation.type === BELONGS_TO) !==
      undefined
  );
};

function isSystemWithoutType() {
  return (entity: Entity) => {
    if (!isKind('system')(entity)) {
      return false;
    }
    const systemEntity = entity as WitboostSystem;
    return systemEntity && !systemEntity.spec.type;
  };
}

const useEntityPage = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  const isEditorEnabled = configApi.getOptionalBoolean(
    'catalog.editor.enabled',
  );

  const belongsToTaxonomy = getEntityRelations(entity, BELONGS_TO);

  if (belongsToTaxonomy.length) {
    return { entity, isEditorEnabled };
  }

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading, value: parentEntity } = useAsync(async () => {
    if (partOfSystemRelations?.length)
      return catalogApi.getEntityByRef(partOfSystemRelations[0]);
    return undefined;
  }, [catalogApi]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { entities } = useRelatedEntities(parentEntity!, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  return { entity, loading, parentEntity, entities, isEditorEnabled };
};

const useCustomMicrofrontends = (
  entity?: Entity,
): CustomMicrofrontendOptions[] => {
  const customFrontendApi = useApi(customFrontendApiRef);

  const customPagesConfiguration = useBuilderCustomPagesConfig(
    BuilderCustomPagesEnum.ComponentPage,
  );

  return useMemo(() => {
    if (!customPagesConfiguration.data || !entity) return [];

    return customFrontendApi.makeCustomMicrofrontends(
      customPagesConfiguration.data,
    )(entity);
  }, [customPagesConfiguration.data, entity, customFrontendApi]);
};

const EditorRoute = () => {
  const { entity, loading = false, parentEntity, entities } = useEntityPage();
  const versionedEntity = validateWitboostVersionedEntity(
    parentEntity ?? entity,
  );

  const [version, setVersion] = useState(versionedEntity.spec.mesh.version);

  useEffect(() => {
    setVersion(versionedEntity.spec.mesh.version);
  }, [versionedEntity]);

  return (
    <>
      {loading ? (
        <EditorComponentPageLoader />
      ) : (
        <EditorPageContextProvider
          entity={entity}
          relatedEntities={entities ?? []}
          parent={parentEntity ?? undefined}
          version={version}
          setVersion={setVersion}
        >
          <EditorPage />
        </EditorPageContextProvider>
      )}
    </>
  );
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasReservedFieldsError}>
        <Grid item xs={12}>
          <ReservedFieldsError />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12}>
      <EntityAboutCard />
    </Grid>
    <Grid item xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>
  </Grid>
);

const OverViewContent = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const { allowRefresh } = useRefreshPermission(entity);
  const refreshEntity = createRefreshEntityFunction({
    entity,
    catalogApi,
    alertApi,
  });
  return (
    <CustomView
      id="builder_component"
      transform="builder_system"
      typeId={String(entity.spec?.type ?? '')}
      templateId={(entity.spec?.mesh as any | undefined)?.useCaseTemplateId}
      data={{
        entity: entity as WitboostSystem,
        allowRefresh,
      }}
      actions={{
        refreshEntity: refreshEntity,
      }}
    />
  );
};

const ServiceEntityPage = () => {
  const { isEditorEnabled, entity } = useEntityPage();

  const customPages: CustomMicrofrontendOptions[] =
    useCustomMicrofrontends(entity);

  const useCaseTemplateId = (entity.spec?.mesh as any)?.useCaseTemplateId;
  const type = entity.spec?.type as string;
  const { showPage } = useMicrofrontendVisibility(useCaseTemplateId, type);

  return (
    <EntityLayoutWrapper>
      <EntityLayout.Route path="/" title="Overview">
        <OverViewContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6} xs={12}>
            <EntityDependsOnComponentsCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityDependsOnResourcesCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        <TechdocsWrapper entity={entity} />
      </EntityLayout.Route>

      {isEditorEnabled && (
        <EntityLayout.Route path="/editor" title="Edit and test">
          <EditorRoute />
        </EntityLayout.Route>
      )}

      {customPages.map(
        page =>
          showPage(page) && (
            <EntityLayout.Route path={page.route} title={page.title}>
              <CustomMicrofrontendComponent key={page.identifier} {...page} />
            </EntityLayout.Route>
          ),
      )}
    </EntityLayoutWrapper>
  );
};

const WebsiteEntityPage = () => {
  const { isEditorEnabled, entity } = useEntityPage();
  const customPages: CustomMicrofrontendOptions[] =
    useCustomMicrofrontends(entity);
  const useCaseTemplateId = (entity.spec?.mesh as any)?.useCaseTemplateId;
  const type = entity.spec?.type as string;
  const { showPage } = useMicrofrontendVisibility(useCaseTemplateId, type);

  return (
    <EntityLayoutWrapper>
      <EntityLayout.Route path="/" title="Overview">
        <OverViewContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6} xs={12}>
            <EntityDependsOnComponentsCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityDependsOnResourcesCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        <TechdocsWrapper entity={entity} />
      </EntityLayout.Route>

      {isEditorEnabled && (
        <EntityLayout.Route path="/editor" title="Edit and test">
          <EditorRoute />
        </EntityLayout.Route>
      )}

      {customPages.map(
        page =>
          showPage(page) && (
            <EntityLayout.Route path={page.route} title={page.title}>
              <CustomMicrofrontendComponent key={page.identifier} {...page} />
            </EntityLayout.Route>
          ),
      )}
    </EntityLayoutWrapper>
  );
};

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const ComponentDefaultEntityPage = () => {
  const { isEditorEnabled, entity } = useEntityPage();
  const customPages: CustomMicrofrontendOptions[] =
    useCustomMicrofrontends(entity);

  const useCaseTemplateId = (entity.spec?.mesh as any)?.useCaseTemplateId;
  const type = entity.spec?.type as string;
  const { showPage } = useMicrofrontendVisibility(useCaseTemplateId, type);

  return (
    <EntityLayoutWrapper>
      <EntityLayout.Route path="/" title="Overview">
        <OverViewContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        <TechdocsWrapper entity={entity} />
      </EntityLayout.Route>

      {isEditorEnabled && (
        <EntityLayout.Route
          path="/editor"
          title="Edit and Test"
          tabProps={{ style: { textTransform: 'none' } }}
          overrideHeaderActions={
            <WbHeaderActions>
              <WbEnvironmentSelector />
            </WbHeaderActions>
          }
        >
          <EditorRoute />
        </EntityLayout.Route>
      )}
      {customPages.map(
        page =>
          showPage(page) && (
            <EntityLayout.Route path={page.route} title={page.title}>
              <CustomMicrofrontendComponent key={page.identifier} {...page} />
            </EntityLayout.Route>
          ),
      )}
    </EntityLayoutWrapper>
  );
};

const HeadlessComponentPage = () => {
  const { isEditorEnabled, entity } = useEntityPage();
  const versionedEntity = validateWitboostVersionedEntity(entity);
  const customPages: CustomMicrofrontendOptions[] =
    useCustomMicrofrontends(entity);
  const [_, setVersion] = useState(versionedEntity.spec.mesh.version);
  const useCaseTemplateId = (entity.spec?.mesh as any)?.useCaseTemplateId;
  const type = entity.spec?.type as string;
  const { showPage } = useMicrofrontendVisibility(useCaseTemplateId, type);

  return (
    <EntityLayoutWrapper isWrappingProject>
      <EntityLayout.Route path="/" title="Overview">
        <OverViewContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        <TechdocsWrapper entity={entity} />
      </EntityLayout.Route>

      {isEditorEnabled && (
        <EntityLayout.Route
          path="/editor"
          title="Edit and test"
          overrideHeaderActions={
            <WbHeaderActions>
              <WbEnvironmentSelector />
            </WbHeaderActions>
          }
        >
          <EditorRoute />
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/deploy" title="Control Panel">
        <ControlPanelPage entity={entity} setVersion={setVersion} />
      </EntityLayout.Route>

      {isEditorEnabled && (
        <EntityLayout.Route path="/release" title="Deployment">
          <ReleasePageContextProvider
            entity={entity}
            relatedEntities={[]}
            setVersion={setVersion}
          >
            <ReleasePage />
          </ReleasePageContextProvider>
        </EntityLayout.Route>
      )}

      {customPages.map(
        page =>
          showPage(page) && (
            <EntityLayout.Route path={page.route} title={page.title}>
              <CustomMicrofrontendComponent key={page.identifier} {...page} />
            </EntityLayout.Route>
          ),
      )}
    </EntityLayoutWrapper>
  );
};

const defaultEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      <ServiceEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      <WebsiteEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isHeadlessComponent}>
      <HeadlessComponentPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <ComponentDefaultEntityPage />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const serviceAccountPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12}>
          <AccessTokenCard />
        </Grid>
        <Grid item xs={12}>
          <GitTokensCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const userPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const groupPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const systemPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={12} xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const blueprintPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={12} xs={12}>
          <EntityAboutCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const templatePage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={12} xs={12}>
          <EntityAboutCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const domainPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const hierarchyEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={12} xs={12}>
          <EntityAboutCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const projectPage = <ProjectPage title="Project" />;
export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isServiceAccount()} children={serviceAccountPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isSystemWithoutType()} children={systemPage} />
    <EntitySwitch.Case if={isKind('system')} children={projectPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('blueprint')} children={blueprintPage} />
    <EntitySwitch.Case if={isKind('edittemplate')} children={templatePage} />
    <EntitySwitch.Case if={isKind('template')} children={templatePage} />
    <EntitySwitch.Case
      if={isKind('hierarchy')}
      children={hierarchyEntityPage}
    />
    <EntitySwitch.Case
      if={isKind('hierarchyclass')}
      children={hierarchyEntityPage}
    />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
